import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';
import { 
  MessageCircle, 
  CheckSquare, 
  Activity, 
  Moon, 
  Calendar,
  Sparkles,
  Heart,
  Film,
  Wind,
  Brain,
  BookOpen,
  GraduationCap,
  Users,
  Target,
  LineChart,
  Lightbulb,
  Shield,
  Zap,
  TrendingUp,
  Award,
  Apple,
  Flame,
  Database,
  BarChart3,
  Eye,
  Dumbbell,
  ArrowLeft,
  Gem
} from 'lucide-react';
import therappiWhoopCombined from '@/assets/therappi-whoop-combined.svg';
import { cn } from '@/lib/utils';

interface Tool {
  id: string;
  label: string;
  description: string;
  route: string;
  icon: any;
  category: 'therapy' | 'tracking' | 'wellness' | 'growth' | 'insights' | 'health';
  featured?: boolean;
  badge?: string;
}

const TOOLS: Tool[] = [
  // Therapy & Support
  { id: 'therapy', label: 'AI Reflection', description: 'Talk to your AI companion for intelligent guidance tailored to your needs', route: '/therapy', icon: MessageCircle, category: 'therapy', featured: true },
  { id: 'sanctuary', label: 'The Sanctuary', description: 'Your complete psychological wellness suite with multiple therapy modes', route: '/sanctuary', icon: Shield, category: 'therapy', featured: true },
  { id: 'crisis', label: 'Crisis Support', description: 'Immediate help & resources when you need them most', route: '/crisis', icon: Shield, category: 'therapy' },
  { id: 'mental-wellness', label: 'Mental Wellness', description: 'Mental health tools and assessments', route: '/mental-wellness', icon: Brain, category: 'therapy' },
  
  // Daily Tracking
  { id: 'checkin', label: 'Daily Check-In', description: 'Connect with your inner wisdom and track your wellness journey', route: '/daily-check-in', icon: CheckSquare, category: 'tracking' },
  { id: 'sleep', label: 'Sleep & Dreams', description: 'Monitor your rest and dream patterns', route: '/sleep-and-dreams', icon: Moon, category: 'tracking' },
  { id: 'historical', label: 'Historical Data', description: 'View and analyse your past data', route: '/historical-data', icon: Database, category: 'tracking' },
  
  // Health & Nutrition
  { id: 'nutrition', label: 'Nutrition', description: 'Log food, recipes and track your diet', route: '/nutrition', icon: Apple, category: 'health' },
  { id: 'longevity', label: 'Longevity', description: 'Protocols and lifespan optimisation', route: '/longevity', icon: Zap, category: 'health' },
  { id: 'inflammation', label: 'Inflammation Hub', description: 'Reduce inflammation and track IL scores', route: '/inflammation', icon: Flame, category: 'health' },
  { id: 'wellness-tracks', label: 'Wellness Tracks', description: 'Comprehensive health tracking', route: '/wellness-tracks', icon: Activity, category: 'health' },
  
  // Wellness Practices
  { id: 'breathwork', label: 'Breathwork', description: 'Nervous system regulation and breathing exercises', route: '/breathwork', icon: Wind, category: 'wellness' },
  { id: 'meditation', label: 'Guided Meditations', description: 'Find peace with our meditation collection', route: '/meditations', icon: Brain, category: 'wellness', badge: 'Pro' },
  { id: 'telempathy', label: 'Telempathy Tools', description: 'Intuitive and psychic development practices', route: '/telempathy-tools', icon: Eye, category: 'wellness' },
  { id: 'rock-remedies', label: 'Rock Remedies®', description: 'Crystal healing, moon ceremonies & grounding rituals', route: '/rock-remedies', icon: Gem, category: 'wellness' },
  
  // Growth & Development
  { id: 'future-self', label: 'Future Self Spotlight', description: 'Timeline transformation and future self alignment', route: '/future-self', icon: Sparkles, category: 'growth' },
  { id: 'manifestation', label: 'Manifestation Magnifier', description: 'Create your personalised manifestation movie', route: '/manifestation', icon: Film, category: 'growth' },
  { id: 'courses', label: 'Wellness Courses', description: 'Guided programs for personal growth', route: '/courses', icon: GraduationCap, category: 'growth', badge: 'Pro' },
  { id: 'community', label: 'Connect & Grow', description: 'Community features and support', route: '/connect-and-grow', icon: Users, category: 'growth' },
  { id: 'relational', label: 'Relational Hub', description: 'Self-connection & relationship exercises', route: '/relational', icon: Heart, category: 'growth' },
  
  // Insights & Analysis
  { id: 'vault', label: 'Personal Library', description: 'Your insights vault and saved content', route: '/vault', icon: LineChart, category: 'insights' },
  { id: 'correlations', label: 'Correlations', description: 'Discover mind-body patterns and connections', route: '/vault?tab=correlations', icon: TrendingUp, category: 'insights' },
  { id: 'mind-body', label: 'Mind-Body Insights', description: 'Connection analysis between mind and body', route: '/mind-body-insights', icon: Lightbulb, category: 'insights' },
  { id: 'insights', label: 'AI Insights', description: 'Premium analysis and recommendations', route: '/insights', icon: Lightbulb, category: 'insights' },
  { id: 'my-scores', label: 'My Scores', description: 'Track your health metrics and scores', route: '/my-scores', icon: BarChart3, category: 'insights' },
];

const CATEGORIES = [
  { id: 'therapy', label: 'Therapy & Support', color: 'from-primary/10 to-primary/5', borderColor: 'border-primary/30' },
  { id: 'tracking', label: 'Daily Tracking', color: 'from-accent/10 to-accent/5', borderColor: 'border-accent/30' },
  { id: 'health', label: 'Health & Nutrition', color: 'from-primary/10 to-primary/5', borderColor: 'border-primary/30' },
  { id: 'wellness', label: 'Wellness Practices', color: 'from-secondary/10 to-secondary/5', borderColor: 'border-secondary/30' },
  { id: 'growth', label: 'Growth & Development', color: 'from-primary/10 to-primary/5', borderColor: 'border-primary/30' },
  { id: 'insights', label: 'Insights & Analysis', color: 'from-accent/10 to-accent/5', borderColor: 'border-accent/30' },
];

export default function AllTools() {
  const navigate = useNavigate();

  // Get featured tools for the top section
  const featuredTools = TOOLS.filter(tool => tool.featured);

  return (
    <div className="min-h-screen bg-gradient-therapeutic">
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-foreground mb-4">
              All Tools
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Your complete wellness toolkit. Choose any tool to begin your journey.
            </p>
          </div>

          {/* WHOOP Integration Card */}
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 mb-8 sm:mb-12">
            <div className="sm:col-span-8 sm:col-start-3">
              <Card className={cn(
                "p-0 bg-gradient-therapeutic shadow-therapy border-0 overflow-hidden hover:shadow-gentle transition-all duration-300"
              )}>
                <Link to="/whoop-dashboard" className="block p-8 group relative">
                  <img 
                    src={therappiWhoopCombined} 
                    alt="Therappi x WHOOP" 
                    className="h-auto object-contain mx-auto hover:scale-105 transition-transform duration-300" 
                    style={{ width: '100%', maxWidth: '500px' }} 
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-transparent group-hover:bg-muted/10 transition-colors">
                    <p className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                      View your WHOOP data
                    </p>
                  </div>
                </Link>
              </Card>
            </div>
          </div>

          {/* Featured Tools - Large Cards */}
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {featuredTools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Card 
                  key={tool.id}
                  className={cn(
                    "p-4 sm:p-6 shadow-therapy border-2 border-primary/30 hover:border-primary/60",
                    "hover:shadow-gentle transition-all duration-300 hover:scale-[1.02]",
                    "bg-gradient-to-br from-primary/10 to-primary/5 cursor-pointer"
                  )}
                  onClick={() => navigate(tool.route)}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center shadow-lg">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <h3 className="text-xl font-light text-foreground">{tool.label}</h3>
                  </div>
                  <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                    {tool.description}
                  </p>
                  <Button className="w-full" size="lg">
                    {tool.id === 'therapy' ? 'Start Chat' : 'Enter Sanctuary'}
                  </Button>
                </Card>
              );
            })}
          </div>

          {/* Tools Grid by Category */}
          <div className="space-y-10 sm:space-y-12">
            {CATEGORIES.map((category) => {
              const categoryTools = TOOLS.filter(tool => tool.category === category.id && !tool.featured);
              
              if (categoryTools.length === 0) return null;
              
              return (
                <div key={category.id}>
                  <h2 className="text-xl sm:text-2xl font-light text-foreground mb-4 sm:mb-6">
                    {category.label}
                  </h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {categoryTools.map((tool) => {
                      const Icon = tool.icon;
                      
                      return (
                        <Card 
                          key={tool.id}
                          className={cn(
                            "p-4 sm:p-6 shadow-therapy border-2 hover:shadow-gentle transition-all duration-300 hover:scale-[1.02] cursor-pointer relative",
                            `bg-gradient-to-br ${category.color}`,
                            category.borderColor,
                            "hover:border-primary/60"
                          )}
                          onClick={() => navigate(tool.route)}
                        >
                          {tool.badge && (
                            <div className="absolute top-3 right-3">
                              <span className="text-xs bg-accent text-accent-foreground px-2 py-1 rounded-full">
                                {tool.badge}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
                              <Icon className="h-5 w-5 text-primary-foreground" />
                            </div>
                            <h3 className="text-lg font-light text-foreground">{tool.label}</h3>
                          </div>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {tool.description}
                          </p>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Back to Dashboard */}
          <div className="mt-12 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
